<template>
    <div class="participants" v-touch:swipe.right="swipeHandler">
        <div>
            <div class="participants__title">
                <h3>{{ competition.name }}</h3>
                <p class="mat4">{{ competition.location }} - {{ competition.startedTime.format('DD.MM.YYYY') }}</p>
            </div>
        </div>
        <base-input-select :clearable="false" class="participants__classifications" :value="selectedClassification"
            @input="
                SET_SELECTED_CLASSIFICATION_ID($event.id),
                eventBus.$emit('selectedClassification')
                " :options="classifications" label="namePl" />
        <div>
            <base-input-autocomplete class="participants__search" :values="players" icon="fas fa-search"
                label="Znajdz zawodnika" @select-player="onSelectPlayer" />
        </div>

        <div class="participants__menu">
            <div>
                <div class="participants__menu-btnSettings" @click="showSubMenu = !showSubMenu">
                    <i class="fa fa-cogs" :style="showSubMenu ? 'color: rgb(84, 110, 122);' : ''" />
                </div>
            </div>
            <transition name="roll">
                <template v-if="showSubMenu">
                    <div class="participants__menu-options">
                        <div class="switch" @click="SET_SHOW_TAILS(!isShowDeviceTails)">
                            <div class="switch-btn" :class="{
                                'switch-btn--active': isShowDeviceTails,
                            }"></div>
                            <div class="switch-label">
                                Rysuj ścieżki na żywo
                            </div>
                        </div>
                        <div class="switch" @click="SET_SHOW_ALL_NAMES(!isShowAllNames)">
                            <div class="switch-btn" :class="{
                                'switch-btn--active': isShowAllNames,
                            }"></div>
                            <div class="switch-label">
                                Pokaż wszystkie nazwy zawodników
                            </div>
                        </div>
                        <div class="switch" :class="{ disabled: !isTrackAvailable }" @click="isTrackAvailable
                            ? SET_SHOW_DISTANCE(!isShowDistanceMarkers)
                            : ''">
                            <div class="switch-btn" :class="{
                                'switch-btn--active': isShowDistanceMarkers,
                            }"></div>
                            <div class="switch-label">
                                Pokaż tabliczki z dystansem
                            </div>
                        </div>
                        <div class="switch" :class="{ disabled: !isShowDistanceMarkers || !isTrackAvailable }" @click="
                            (isShowDistanceMarkers && isTrackAvailable)
                                ? SET_REVERT_DISTANCE_MARKERS(
                                    !isRevertDistanceMarkers
                                )
                                : ''
                            ">
                            <div class="switch-btn" :class="{
                                'switch-btn--active': isRevertDistanceMarkers,
                            }"></div>
                            <div class="switch-label">
                                Odwróć kolejność tabliczek
                            </div>
                        </div>
                        <div class="participants__menu-ratio"
                            :class="{ disabled: !isShowDistanceMarkers || !isTrackAvailable }">
                            <div class="col-6 pa0">Liczba tabliczek</div>
                            <div class="col-6 pa0">
                                <base-input-select :disabled="!isShowDistanceMarkers || !isTrackAvailable"
                                    class="participants__ratio-select" :clearable="false" :value="amountDistanceMarkers"
                                    @input="SET_AMOUNT_DISTANCE_MARKERS($event)" :options="ratioDistance" />
                            </div>
                        </div>
                        <div class="participants__menu-ratio">
                            <div class="col-6 pa0">Kolejność listy</div>
                            <div class="col-6 pa0">
                                <base-input-select class="participants__ratio-select" :clearable="false"
                                    :value="listOrderBy" @input="SET_LIST_ORDER_BY($event)"
                                    :options="listOrderByOptions" />
                            </div>
                        </div>
                    </div>
                </template>
            </transition>
        </div>
        <div>
            <div class="participants__types">
                <base-button-1 class="participants__types-all" :clicked="!isHighlightedDevicesView" @click="
                    SET_DEVICES_VIEW((isHighlightedView = false)),
                    SET_FOCUS_DEVICE_ID(null)
                    ">Wszyscy</base-button-1>
                <base-button-1 class="participants__types-awarded" :clicked="isHighlightedDevicesView" @click="
                    SET_DEVICES_VIEW((isHighlightedView = true)),
                    SET_FOCUS_DEVICE_ID(null)
                    ">Wyróżnieni</base-button-1>
            </div>
        </div>
        <div class="participants__users">
            <base-device v-for="(player, idx) in playersOrdered" v-show="player.isVisible"
                :class="{ mab16: idx + 1 === playersOrdered.length }" :device="player._device()"
                :disabled="!player.isLocatedInTrackZone" :key="idx" :player="player" :ref="player.id"
                @changeFocus="onDeviceClickHandler()" />
        </div>
        <base-button-1 class="participants__types-close" :clicked="!isHighlightedDevicesView"
            @click="$emit('update:isMenu', !isMenu)">
            Pokaż mape <i class="fas fa-map"></i>
        </base-button-1>
    </div>
</template>

<script>
import BaseButton1 from '@/components/BaseButton1'
import BaseInputAutocomplete from '@/components/BaseInputAutocomplete'
import BaseDevice from '@/components/BaseDevice'
import { mapGetters, mapMutations } from 'vuex'
import {
    SET_DEVICES_VIEW,
    SET_AWARDED_DEVICES,
    SET_SELECTED_CLASSIFICATION_ID,
    SET_FOCUS_DEVICE_ID,
    SET_AMOUNT_DISTANCE_MARKERS,
    SET_LIST_ORDER_BY,
    SET_SHOW_DISTANCE,
    SET_SHOW_TAILS,
    SET_REVERT_DISTANCE_MARKERS,
    SET_SHOW_ALL_NAMES,
} from '@/store/mutations.type'
import _ from 'lodash'
import BaseInputSelect from '@/components/BaseInputSelect'
import { eventBus } from '@/main'
export default {
    components: {
        BaseInputAutocomplete,
        BaseButton1,
        BaseDevice,
        BaseInputSelect,
    },
    props: {
        isMenu: {
            type: Boolean,
            required: true,
        },
        classifications: {
            type: Array,
            required: true,
        },
        players: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            inputText: '',
            eventBus: eventBus,
            showDistance: false,
            showSubMenu: false,
            ratioDistance: [5, 10, 20, 30, 50, 100],
            listOrderByOptions: ['Nazwisko', 'Numer'],
        }
    },
    computed: {
        ...mapGetters([
            'awardedDeviceIds',
            'isHighlightedDevicesView',
            'selectedClassificationId',
            'competitions',
            'focusDeviceId',
            'playbackMarkers',
            'isPlaybackReady',
            'markers',
            'amountDistanceMarkers',
            'listOrderBy',
            'isShowDistanceMarkers',
            'isShowDeviceTails',
            'isRevertDistanceMarkers',
            'isShowAllNames',
            'isLiveMode',
            'playbackMarkers',
            'gpxFiles',
        ]),
        isTrackAvailable() {
            return !!this.gpxFiles[this.selectedClassificationId].url
        },
        selectedClassification() {
            return _.find(
                this.classifications,
                (classification) =>
                    classification.id === this.selectedClassificationId
            )
        },
        competition() {
            return this.competitions[this.classifications[0].competition]
        },
        playersOrdered() {
            let playersList = this.players
            if (this.listOrderBy === 'Nazwisko') {
                return playersList.sort((a, b) => {
                    const nameA = `${a.lastname || ''} ${a.firstname || ''}`.trim()
                    const nameB = `${b.lastname || ''} ${b.firstname || ''}`.trim()
                    return nameA.localeCompare(nameB, 'pl', { sensitivity: "base" })
                })
            } else {
                return playersList.sort((a, b) => (a.playerNumber - b.playerNumber))
            }
        }
    },
    methods: {
        ...mapMutations({
            SET_DEVICES_VIEW,
            SET_AWARDED_DEVICES,
            SET_SELECTED_CLASSIFICATION_ID,
            SET_FOCUS_DEVICE_ID,
            SET_AMOUNT_DISTANCE_MARKERS,
            SET_LIST_ORDER_BY,
            SET_SHOW_DISTANCE,
            SET_SHOW_TAILS,
            SET_REVERT_DISTANCE_MARKERS,
            SET_SHOW_ALL_NAMES,
        }),
        swipeHandler() {
            this.$emit('update:isMenu', false)
        },
        onDeviceClickHandler() {
            if (window.innerWidth < 768) {
                this.$emit('update:isMenu', false)
            }
        },
        scrollToPlayer(player) {
            this.$refs[player.id][0].$el.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            })
        },
        onSelectPlayer(player) {
            if (this.isHighlightedDevicesView) {
                this.SET_DEVICES_VIEW(false)
            }
            this.scrollToPlayer(player)
            if (player.isVisible && player.isLocatedInTrackZone) {
                this.onDeviceClickHandler()
            }
            if (this.focusDeviceId && this.isPlaybackReady) {
                this.playbackMarkers[this.focusDeviceId].closePopup()
            }
            if (this.selectedClassificationId === player.classification) {
                if (player.isVisible && player.isLocatedInTrackZone) {
                    this.SET_FOCUS_DEVICE_ID(player.deviceId)
                } else {
                    this.SET_FOCUS_DEVICE_ID(null)
                }
            } else {
                if (player.classification) {
                    this.SET_SELECTED_CLASSIFICATION_ID(player.classification)
                }
                if (player.isVisible && player.isLocatedInTrackZone) {
                    this.SET_FOCUS_DEVICE_ID(player.deviceId)
                } else {
                    this.SET_FOCUS_DEVICE_ID(null)
                }
            }
        },
    },
    watch: {
        isShowAllNames() {
            if (this.isLiveMode) {
                for (let marker of Object.values(this.markers)) {
                    if (this.isShowAllNames) {
                        marker.openPopup()
                    } else {
                        marker.closePopup()
                    }
                }
            } else {
                // Playback
                for (let marker of Object.values(this.playbackMarkers)) {
                    if (this.isShowAllNames) {
                        marker.openTooltip()
                    } else {
                        marker.closeTooltip()
                    }
                }
            }
        },
    },
    mounted() {
        // Get awarded device ids from web storage.
        let awardedDeviceIds = JSON.parse(
            sessionStorage.getItem('awardedDeviceIds')
        )
        if (awardedDeviceIds) {
            this.SET_AWARDED_DEVICES(awardedDeviceIds)
        }
        setTimeout(() => {
            let noEmpty = []
            this.classifications.forEach((classId) => {
                if (this.players.filter(player => player.classification == classId.id).length > 0) {
                    noEmpty.push(classId.id)
                }
            }
            )
            if (!noEmpty.includes(this.selectedClassificationId)) { this.SET_SELECTED_CLASSIFICATION_ID(noEmpty[0]) }
        }, 1000 * 3)
    },
}
</script>

<style lang="scss">
.disabled {
    opacity: 0.5;
}

// Participants
.participants {
    padding: 16px 8px !important;
    display: flex;
    flex-direction: column;
    height: 100vh;
    max-height: 100vh;

    // Title.
    &__title {
        display: flex;
        flex-direction: column;
        margin-left: 8px;
        margin-right: 8px;
        justify-content: space-between;
    }

    // Search input.
    &__search {
        margin-top: 16px;
        margin-left: 8px;
        margin-right: 48px;
    }

    // Types buttons.
    &__types {
        padding: 0 8px;
        display: flex;
        margin-top: 16px;

        &-all {
            margin-right: 2px;
        }

        &-awarded {
            margin-left: 2px;
        }

        &-close {
            position: fixed;
            bottom: 8px;
            gap: 8px;

            @media (min-width: 768px) {
                display: none;
            }

            max-width: 95%;
            background-color: #546e7a;
            color: #fff;
            box-shadow: 0 8px 12px 0 rgba(0, 0, 0, .2);

            &:hover {
                background-color: #546e7a;
            }
        }
    }

    // Users box
    &__users {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-top: 12px;
        padding: 4px 8px 16px 8px;
        overflow-y: auto;
        overflow-x: hidden;
        scrollbar-width: thin;
    }

    // classifications select.
    &__classifications {
        margin-left: 8px;
        margin-right: 8px;
        margin-top: 16px;
    }

    &__menu {
        position: relative;
        margin-top: 3px;
        margin-left: 8px;

        &-btn {
            display: block;
            position: absolute;
            right: 8px;
            top: 0;
            text-decoration: underline;
            cursor: pointer;
        }

        &-btnSettings {
            display: block;
            position: absolute;
            right: 14px;
            top: -38px;
            font-size: 20px;
            text-decoration: none;
            cursor: pointer;
        }

        &-options {
            display: flex;
            flex-direction: column;
            margin-top: 8px;
        }

        &-distance {
            display: flex;
            padding-left: 8px;
        }

        &-ratio {
            display: flex;
            align-items: center;
        }

        &-revert {
            display: flex;
            padding-left: 8px;
            padding-bottom: 15px;
        }
    }

    &__ratio-select {
        margin-left: 15px;
        margin-right: 8px;
    }
}

.switch {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
    padding: 5px 0;

    &-label {
        cursor: pointer;
    }

    &-btn {
        min-width: 40px;
        height: 24px;
        background-color: $pale-grey;
        border-radius: 12px;
        position: relative;
        margin-right: 15px;
        cursor: pointer;
        transition: all 0.3s;

        &::after {
            transition: all 0.3s;
            content: '';
            position: absolute;
            top: 2px;
            left: 2px;
            width: 20px;
            height: 20px;
            border-radius: 12px;
            display: inline-block;
            background-color: $white;
            box-shadow: 0 8px 12px 0 rgba(0, 0, 0, 0.1),
                0 0 2px 0 rgba(55, 71, 79, 0.2);
        }

        &--active {
            background-color: $red-orange;
            opacity: 1;

            &::after {
                left: 18px;
            }
        }
    }
}

.c-pointer {
    cursor: pointer;
}

.roll-enter {
    max-height: 0;
}

.roll-enter-to {
    max-height: 200px;
}

.roll-leave {
    max-height: 200px;
}

.roll-leave-to {
    max-height: 0;
}

.roll-enter-active,
.roll-leave-active {
    transition: all 0.8s;
    overflow: hidden;
}
</style>
